import { ReactNode } from 'react';
import { Form as FormikForm } from 'formik';
import InputMask from 'react-input-mask';
import { PHONE_COUNTRIES, PhoneCountryCode } from 'v3/utils/phoneCountries';
import FlowScreen from 'v3/components/FlowScreen';
import * as S from './gettingStartedStyles';
import { ProcessingIcon } from './styles';

/**
 * Getting started (dark) — the single sign-up screen for step 0. It used to be
 * identity capture only, while gender, address, SSN and consent lived in a second
 * light panel; now everything lives here, and the intermediate voucher panel moved
 * to ReviewOffer.
 *
 * This component IS Formik's <form>: the CTA is a real submit, firing the
 * startDeal() that already existed. The migrated sections arrive via `children`,
 * mounted in StartDeal — so the design-system components stay inside Formik's
 * render prop, with no prop drilling.
 *
 * Names in three fields, mirroring the backend (`first_name` and `last_name`
 * required, `middle_name` optional). It used to be a single "Full Name" field split
 * on whitespace, which corrupted compound names ("Ana María Rodríguez Pérez" became
 * first name "Ana", last name "Pérez") — and that name goes onto the contract.
 */

const c = {
  title: 'Getting started',
  subtitle: 'A few quick details to see your options.',
  firstName: 'First name',
  firstNamePlaceholder: 'First name',
  middleName: 'Middle name',
  middleNamePlaceholder: 'Optional',
  lastName: 'Last name',
  lastNamePlaceholder: 'Last name',
  dob: 'Date of birth',
  dobPlaceholder: 'MM/DD/YYYY',
  phone: 'Phone number',
  email: 'Email',
  emailPlaceholder: 'e.g. name@email.com',
  cta: 'Show my offer',
  /* Nothing is being simulated — the CTA posts to the contract flow and waits for a real
     offer. It said "Simulating your offer", which is both wrong and, with no motion beside
     it, indistinguishable from a frozen page on a slow request. */
  ctaLoading: 'Getting your offer…',
};

/** ISO alpha-2 (e.g. "US") -> flag emoji via regional-indicator code points. */
const flagEmoji = (code: string) =>
  code
    .toUpperCase()
    .replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)));

interface GettingStartedProps {
  values: any;
  errors: any;
  touched: any;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  phoneCountry: PhoneCountryCode;
  setPhoneCountry: (code: PhoneCountryCode) => void;
  isLoading?: boolean;
  /** Address guard that lived in the details panel's submit. */
  onCtaClick?: () => void;
  /** Gender, address, SSN and consent, mounted in StartDeal. */
  children?: ReactNode;
}

const GettingStarted = ({
  values,
  errors,
  touched,
  setFieldValue,
  phoneCountry,
  setPhoneCountry,
  isLoading = false,
  onCtaClick,
  children,
}: GettingStartedProps) => {
  const country = PHONE_COUNTRIES[phoneCountry];

  /* With no intermediate panel there is no partial validation: Formik marks
     everything touched on submit, so `touched` alone governs what to show. */
  const fieldError = (key: string) =>
    touched[key] && errors[key] ? errors[key] : undefined;

  return (
    <FlowScreen>
      <S.Center as={FormikForm}>
        {/* Eyebrow outside the card, title inside — same pattern as the other steps. */}
        <S.Eyebrow>
          <span className="phase">Onboarding</span>
          <span className="rest"> · Step 1 of 3</span>
        </S.Eyebrow>

        <S.Card>
          <S.Title>{c.title}</S.Title>
          <S.Subtitle>{c.subtitle}</S.Subtitle>

          <S.Fields>
            <S.Row>
              <S.FieldCol>
                <S.Label htmlFor="gs-firstName">
                  {c.firstName} <span>*</span>
                </S.Label>
                <input
                  id="gs-firstName"
                  name="firstName"
                  className="sc-dark-field"
                  autoComplete="given-name"
                  placeholder={c.firstNamePlaceholder}
                  value={values.firstName}
                  aria-invalid={Boolean(fieldError('firstName'))}
                  onChange={(e) => setFieldValue('firstName', e.target.value)}
                />
                {fieldError('firstName') ? (
                  <S.ErrorText>{fieldError('firstName')}</S.ErrorText>
                ) : null}
              </S.FieldCol>

              <S.FieldCol>
                <S.Label htmlFor="gs-lastName">
                  {c.lastName} <span>*</span>
                </S.Label>
                <input
                  id="gs-lastName"
                  name="lastName"
                  className="sc-dark-field"
                  autoComplete="family-name"
                  placeholder={c.lastNamePlaceholder}
                  value={values.lastName}
                  aria-invalid={Boolean(fieldError('lastName'))}
                  onChange={(e) => setFieldValue('lastName', e.target.value)}
                />
                {fieldError('lastName') ? (
                  <S.ErrorText>{fieldError('lastName')}</S.ErrorText>
                ) : null}
              </S.FieldCol>
            </S.Row>

            <S.Row>
              <S.FieldCol>
                {/* Opcional no backend (`middle_name`: required=False). */}
                <S.Label htmlFor="gs-middleName">{c.middleName}</S.Label>
                <input
                  id="gs-middleName"
                  name="middleName"
                  className="sc-dark-field"
                  autoComplete="additional-name"
                  placeholder={c.middleNamePlaceholder}
                  value={values.middleName}
                  onChange={(e) => setFieldValue('middleName', e.target.value)}
                />
              </S.FieldCol>

              <S.FieldCol>
                <S.Label htmlFor="gs-dob">
                  {c.dob} <span>*</span>
                </S.Label>
                <InputMask
                  mask="99/99/9999"
                  maskChar=""
                  value={values.dateOfBirth}
                  onChange={(e) => setFieldValue('dateOfBirth', e.target.value)}
                  id="gs-dob"
                  name="dateOfBirth"
                  className="sc-dark-field"
                  inputMode="numeric"
                  placeholder={c.dobPlaceholder}
                  aria-invalid={Boolean(fieldError('dateOfBirth'))}
                />
                {fieldError('dateOfBirth') ? (
                  <S.ErrorText>{fieldError('dateOfBirth')}</S.ErrorText>
                ) : null}
              </S.FieldCol>
            </S.Row>

            <S.Row>
              <S.FieldCol>
                <S.Label htmlFor="gs-phone">
                  {c.phone} <span>*</span>
                </S.Label>
                <S.PhoneBox
                  className="sc-dark-fieldset"
                  data-invalid={fieldError('userPhone') ? 'true' : undefined}
                >
                  <S.CountryTrigger>
                    <span aria-hidden>{flagEmoji(country.code)}</span>
                    <span>+{country.dialCode}</span>
                    <span aria-hidden style={{ opacity: 0.6 }}>
                      ▾
                    </span>
                    <select
                      aria-label="Phone country"
                      value={phoneCountry}
                      onChange={(e) =>
                        setPhoneCountry(e.target.value as PhoneCountryCode)
                      }
                    >
                      {Object.values(PHONE_COUNTRIES).map((co) => (
                        <option key={co.code} value={co.code}>
                          {co.code} +{co.dialCode}
                        </option>
                      ))}
                    </select>
                  </S.CountryTrigger>
                  <S.Divider aria-hidden />
                  <InputMask
                    mask={country.mask}
                    maskChar=""
                    value={values.userPhone}
                    onChange={(e) => setFieldValue('userPhone', e.target.value)}
                    id="gs-phone"
                    name="userPhone"
                    className="sc-dark-input-bare"
                    type="tel"
                    inputMode="numeric"
                    placeholder={
                      country.dialCode === '1'
                        ? '(201) 555-0123'
                        : country.placeholder
                    }
                  />
                </S.PhoneBox>
                {fieldError('userPhone') ? (
                  <S.ErrorText>{fieldError('userPhone')}</S.ErrorText>
                ) : null}
              </S.FieldCol>

              <S.FieldCol>
                <S.Label htmlFor="gs-email">
                  {c.email} <span>*</span>
                </S.Label>
                <input
                  id="gs-email"
                  name="email"
                  className="sc-dark-field"
                  type="email"
                  autoComplete="email"
                  placeholder={c.emailPlaceholder}
                  value={values.email}
                  aria-invalid={Boolean(fieldError('email'))}
                  onChange={(e) => setFieldValue('email', e.target.value)}
                />
                {fieldError('email') ? (
                  <S.ErrorText>{fieldError('email')}</S.ErrorText>
                ) : null}
              </S.FieldCol>
            </S.Row>
          </S.Fields>

          {children}
        </S.Card>

        <S.CtaRow>
          <S.Cta type="submit" disabled={isLoading} onClick={onCtaClick}>
            {isLoading ? (
              <S.LoadingCta>
                <ProcessingIcon size={18} aria-hidden />
                {c.ctaLoading}
              </S.LoadingCta>
            ) : (
              c.cta
            )}
          </S.Cta>
        </S.CtaRow>
      </S.Center>
    </FlowScreen>
  );
};

export default GettingStarted;
